<template>
  <button type="button" class="ui-button" :class="className" :disabled="disabled">
    <div v-if="loading" class="ui-button__loader">
      <IconLoading />
    </div>
    <div v-if="$slots.prepend" class="ui-button__prepend">
      <slot name="prepend" />
    </div>
    <div v-if="$slots.default" class="ui-button__content">
      <slot />
    </div>
  </button>
</template>
<script setup lang="ts">
import { computed } from 'vue'

export interface UiButtonProps {
  disabled?: boolean
  small?: boolean
  icon?: boolean
  loading?: boolean
  stretch?: boolean
  rounded?: boolean
  /** button style variant */
  variant?: 'primary' | 'secondary' | 'link' | 'error' | 'success'
}

const props = defineProps<UiButtonProps>()

// Computed
const className = computed(() => ({
  [`_${props.variant}`]: !!props.variant,
  _small: props.small,
  _icon: props.icon,
  _stretch: props.stretch,
  _rounded: props.rounded,
  '--loading': props.loading,
  '--disabled': props.disabled,
}))
</script>
<style lang="sass" scoped>
@use '@/styles/variables'
@use 'sass:map'

.ui-button
    --button-bg-color: #{map.get(variables.$button-background-color, 'default')}
    --button-color: #{map.get(variables.$button-color, 'default')}
    --button-border-color: #{map.get(variables.$button-border-color, 'default')}
    --button-icon-size: 16px
    --button-height: #{map.get(variables.$button-height, 'default')}
    --button-font-size: #{map.get(variables.$button-typography, 'default', 'size')}
    --button-line-height: #{map.get(variables.$button-typography, 'default', 'line-height')}
    position: relative
    flex-shrink: 0
    display: inline-grid
    align-items: center
    justify-content: center
    grid-template-areas: 'prepend content'
    grid-template-columns: min-content min-content
    text-decoration: none
    max-width: 100%
    min-width: var(--button-height)
    outline: none
    transition-property: background-color, color, border
    transition-duration: variables.$transition-duration
    transition-timing-function: variables.$transition-timing-function
    user-select: none
    vertical-align: middle
    height: var(--button-height)
    padding: 0 variables.$spacer*2
    border-radius: map.get(variables.$border-radius, 'small')
    color: var(--button-color)
    border: 1px solid var(--button-border-color)
    background-color: var(--button-bg-color)
    overflow: hidden
    font-size: var(--button-font-size)
    line-height: var(--button-line-height)
    font-weight: map.get(variables.$button-typography, 'default', 'weight')
    cursor: pointer

    &:active
        --button-bg-color: #{map.get(variables.$button-background-color, 'active')}

    &._icon
        padding: 0

    &._stretch
        flex: 1 0 auto
        min-width: 100%

    &._rounded
        border-radius: 50%

    &._link
        --button-color: #{map.get(variables.$button-color, 'link')}

        &:active
            --button-bg-color: #{map.get(variables.$button-background-color, 'link-active')}

            .ui-button__content
                border-bottom-color: transparent

        .ui-button__content
            border-bottom: 1px solid

    &._small
        --button-height: #{map.get(variables.$button-height, 'small')}
        --button-font-size: #{map.get(variables.$button-typography, 'small', 'size')}
        --button-line-height: #{map.get(variables.$button-typography, 'small', 'line-height')}

    &._primary
        --button-bg-color: #{map.get(variables.$button-background-color, 'primary')}
        --button-color: #{map.get(variables.$button-color, 'primary')}

        &:active
            --button-bg-color: #{map.get(variables.$button-background-color, 'primary-active')}

        &[disabled]
            --button-bg-color: #{map.get(variables.$button-background-color, 'primary-disabled')}

    &._secondary
        --button-bg-color: #{map.get(variables.$button-background-color, 'secondary')}
        --button-border-color: #{map.get(variables.$button-border-color, 'secondary')}

        &:active
            --button-bg-color: #{map.get(variables.$button-background-color, 'secondary-active')}

        &[disabled]
            --button-bg-color: #{map.get(variables.$button-background-color, 'primary-disabled')}

    &._error
        --button-bg-color: #{map.get(variables.$button-background-color, 'secondary')}
        --button-border-color: transparent
        --button-color: #{map.get(variables.$button-color, 'error')}

        &:active
            --button-border-color: #{map.get(variables.$button-border-color, 'error')}

        &[disabled]
            --button-color: #{map.get(variables.$button-color, 'error-disabled')}

    &._success
        --button-bg-color: #{map.get(variables.$button-background-color, 'secondary')}
        --button-border-color: transparent
        --button-color: #{map.get(variables.$button-color, 'success')}

        &:active
            --button-border-color: #{map.get(variables.$button-border-color, 'success')}

        &[disabled]
            --button-color: #{map.get(variables.$button-color, 'success-disabled')}

    &.--loading
        pointer-events: none

        .ui-button__content,
        .ui-button__prepend
            opacity: 0

    &[disabled]
        --button-color: #{map.get(variables.$button-color, 'disabled')}
        pointer-events: none

    &__loader
        position: absolute
        display: flex
        align-items: center
        justify-content: center
        inset: 0

    &__prepend
        grid-area: prepend
        display: flex
        align-items: center
        transition-property: opacity
        transition-duration: variables.$transition-duration
        transition-timing-function: variables.$transition-timing-function
        margin-inline-end: variables.$spacer

    &__content
        grid-area: content
        display: flex
        align-items: center
        white-space: nowrap
        justify-content: center
        transition-property: transform, opacity, border
        transition-duration: variables.$transition-duration
        transition-timing-function: variables.$transition-timing-function

    :deep(svg.icon)
        color: currentColor
        width: var(--button-icon-size)
        height: var(--button-icon-size)
</style>
